import type { ComponentProps } from 'preact';
import { useEffect, useRef } from 'preact/hooks';

import { cn } from '@/lib/utils';
import { ProductInterval } from '@/queries/products/types';

interface ProductIntervalSelectorProps extends Omit<ComponentProps<'div'>, 'children' | 'onChange' | 'value'> {
  value: ProductInterval;
  onChange?: (planInPlanInterval: ProductInterval) => void;
}

const ProductIntervalSelector = ({ className, value, onChange, ...rest }: ProductIntervalSelectorProps) => {
  const indicatorRef = useRef<HTMLSpanElement>(null);

  // animate the indicator when the value changes
  useEffect(() => {
    const span = indicatorRef.current;
    if (!span) return;

    const selectedButton = span.parentElement?.querySelector(`button[aria-checked="true"]`) as HTMLButtonElement;
    if (!selectedButton) {
      span.style.width = '0px';
      span.style.transform = 'translateX(0px)';
      return;
    }

    const buttonWidth = selectedButton.offsetWidth;
    const buttonLeft = selectedButton.offsetLeft;

    span.style.width = `${buttonWidth}px`;
    span.style.transform = `translateX(${buttonLeft}px)`;
  }, [value]);

  return (
    <div
      className={cn('relative flex w-fit items-center rounded-full bg-[#F5F5F4]', className)}
      role="radiogroup"
      aria-label="Subscription plan type"
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    >
      {/* indicator */}
      <span
        ref={indicatorRef}
        className="absolute h-full rounded-full bg-primary-yellow shadow-[0px_5px_7px_0px_#5243C23B] transition duration-300"
      />
      {/* buttons */}
      <button
        type="button"
        role="radio"
        aria-checked={value === 'month'}
        onClick={() => onChange?.('month')}
        className="preflight preflight-button relative rounded-full p-2 text-[9px]/none font-bold text-grey-1"
      >
        MONTHLY
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={value === 'year'}
        onClick={() => onChange?.('year')}
        className="preflight preflight-button relative rounded-full p-2 text-[9px]/none font-bold text-grey-1"
      >
        YEARLY
      </button>
    </div>
  );
};

export default ProductIntervalSelector;
